import {GroupedVirtuoso} from 'react-virtuoso';
import {
    IonContent,
    IonHeader,
    IonItemDivider,
    IonPage,
    IonProgressBar,
    IonTitle,
    IonToolbar,
    IonInfiniteScroll,
    IonInfiniteScrollContent, isPlatform
} from '@ionic/react';
import {IonAvatar, IonItem, IonLabel, IonNote} from '@ionic/react';
import styles from './Log.module.css';
import React, {memo, useMemo, useCallback} from "react";
import IWBrokerClient from "../client/IWBrokerClient";
import {IonInfiniteScrollCustomEvent} from "@ionic/core/dist/types/components";
import UserName from "../components/UserName";
import {useLogQuery} from "../query/logQuery";
import {useGatewayConnectionStateQuery} from "../query/gatewayConnectionStateQuery";

const Log: React.FC = () => {
    const logQuery = useLogQuery();
    const {isConnecting} = useGatewayConnectionStateQuery();
    const logRows = useMemo(() => logQuery.data?.pages.flat() ?? [], [logQuery.data]);
    const {logGroupCounts, logGroups} = useMemo(() => {
            const logGroupCounts: number[] = [];
            const logGroups: string[] = [];
            let lastDate: string | null = null;

            const dateFormat = Intl.DateTimeFormat('default', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
            });

            for (const row of logRows) {
                const date = new Date(row.timestamp);
                const dateStr = dateFormat.format(date)

                if (dateStr !== lastDate) {
                    lastDate = dateStr;
                    logGroups.push(dateStr);
                    logGroupCounts.push(0);
                }

                logGroupCounts[logGroupCounts.length - 1]++;
            }

            return {logGroupCounts, logGroups};
    }, [logRows]);

    const formatTimestamp = useCallback((timestamp: string) => {
        return Intl.DateTimeFormat('default', {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric'
        }).format(new Date(timestamp))
    }, []);

    const loadMore = useCallback(async (event: IonInfiniteScrollCustomEvent<void>) => {
        try {
            await logQuery.fetchNextPage();
        } finally {
            await event.target.complete();
        }
    }, [logQuery]);

    const Footer = useCallback(() => {
        return (
            <IonInfiniteScroll onIonInfinite={loadMore} disabled={!logQuery.hasNextPage} threshold="100px">
                <IonInfiniteScrollContent />
            </IonInfiniteScroll>
        )
    }, [loadMore, logQuery.hasNextPage]);

    function escapeHTML(unsafeText: string) {
        /* Escapes HTML, but preserves \n, unlike React */
        let div = document.createElement('div');
        div.innerText = unsafeText;
        return div.innerHTML;
    }

    return (
        <IonPage>
            <IonHeader translucent={isPlatform('capacitor')} collapse="fade">
                <IonToolbar>
                    <IonTitle>Event Log</IonTitle>
                    {(logQuery.isFetching || isConnecting) && <IonProgressBar type="indeterminate"/>}
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen={true} scrollY={false}>
                <GroupedVirtuoso
                    className="ion-content-scroll-host"
                    groupCounts={logGroupCounts}
                    itemContent={(index: number, groupIndex: number) => {
                        const row = logRows[index];

                        return (
                            <div>
                                <IonItem className={styles.listItem}>
                                    <IonAvatar slot="start">
                                        <img alt="avatar" src={IWBrokerClient.getAvatarURL(row.userId)}/>
                                    </IonAvatar>
                                    <IonLabel class="ion-text-wrap">
                                        <IonNote
                                            className={styles.itemTimestamp}>{formatTimestamp(row.timestamp)}</IonNote>
                                        <h2 className={styles.itemTitle}>{
                                            row.name
                                            || (row.userId && <UserName userId={row.userId} />)
                                            || row.token
                                            || 'Innovation Way'}</h2>
                                        <p dangerouslySetInnerHTML={{__html: escapeHTML(row.message)}} />
                                    </IonLabel>
                                </IonItem>
                            </div>
                        );
                    }}
                    groupContent={(index: number) => {
                        return (
                            <IonItemDivider>{logGroups[index]}</IonItemDivider>
                        );
                    }}
                    components={{ Footer }}
                />
            </IonContent>
        </IonPage>
    );
};

export default memo(Log);
